import React from "react";
import styled from "styled-components";

import { useAllFooterQuery, useAllSocialMediaQuery } from "@api";
import { Container, GridContainer } from "@util/standard";
import { Link, SocialIcon } from "@global";
import { colors, mediaQuery, FONT_PARAGRAPH, SECONDARY_COLOR } from "@util/constants";
import { useStore } from "@util/states";
import { Maybe } from "@graphql-types";
//@ts-ignore
import gdpr from "@static/assets/footer/GDPR.png";
//@ts-ignore
import coppa from "@static/assets/footer/COPPA.png";
// @ts-ignore
import { mainData } from "../locales/dataConst";
// @ts-ignore
import rightArrowIcon from "@static/assets/right-white-arrow.svg";

interface Props {
  pageIban: Maybe<string> | undefined;
}
const LinksContainer = styled(Container)`
  width: 100%;
  justify-content: space-between;
  align-items: flex-end;

  ${mediaQuery.mediumDown} {
    flex-direction: column;
    align-items: flex-start;
  }

  .footer-first-image {
    margin-right: 22px;
  }

  .footer-image-container {
    display: flex;

    ${mediaQuery.mediumDown} {
      margin-top: 36px;
    }
    a {
      display: flex;
    }
  }
`;

const StyledLink = styled(Link)`
  display: block;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 400;
  font-family: ${FONT_PARAGRAPH};
  color: ${colors.white};
  margin: 0 0 15px 0;
  div {
    color: inherit;
  }
`;

const BottomText = styled.p`
  font-family: ${FONT_PARAGRAPH};
  color: rgb(255 255 255 / 80%);
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: 0.03rem;
  width: 528px;

  @media only screen and (max-width: 1024px) {
    width: 328px;
  }

  ${mediaQuery.mediumDown} {
    width: 100%;
  }
`;
const FooterHeadingText = styled.p`
  font-family: "Formula Condensed Bold", sans-serif;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 300;
  color: ${SECONDARY_COLOR};
  letter-spacing: 1px;
  margin-bottom: 15px;
`;

const FooterMainContainer = styled(Container)`
  width: 1440px;
  margin: 54px auto;

  ${mediaQuery.mediumLarge} {
    padding: 0 64px;
  }

  @media only screen and (max-width: 1024px) {
    margin: 45px auto;
    padding: 0 45px;
  }

  ${mediaQuery.smallDown} {
    padding: 0 32px;
    margin: 44px auto;
  }
  @media only screen and (max-width: 400px) and (min-height: 600px) {
    .footer-mobile-li-wrapper:first-child,
    .footer-mobile-li-wrapper:nth-child(2) {
      padding-top: 40px;
    }
    .footer-mobile-li-wrapper:nth-last-child(1),
    .footer-mobile-li-wrapper:last-child {
      padding-bottom: 40px;
    }
  }
  .footer-title-btn-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${SECONDARY_COLOR};

    ${mediaQuery.mediumDown} {
      flex-direction: column;
      align-items: flex-start;
    }
    .footer-title {
      font-weight: 300;
      font-size: 92px;
      line-height: 1.2;
      font-family: "Formula Condensed Bold", sans-serif;

      @media only screen and (max-width: 1024px) {
        font-size: 72px;
        line-height: 1;
      }
    }

    .footer-icon-main-container {
      border: 1px solid #f8f9fa;
      width: auto;
      height: 46px;
      padding: 0 1rem;
      border-radius: 50px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden !important;
      position: relative !important;

      ${mediaQuery.mediumDown} {
        margin-top: 20px;
      }
    }
  }
`;
const Footer = (props: Props) => {
  const { currentRegion } = useStore();
  const { iban } = currentRegion;
  const footerData = useAllFooterQuery(
    iban ? iban : (process.env.GATSBY_DEFAULT_IBAN_REGION as string),
  );
  const socialMediaData = useAllSocialMediaQuery(
    iban ? iban : (process.env.GATSBY_DEFAULT_IBAN_REGION as string),
  );

  if (footerData == null) return null;

  const { links, bottomItems, showSocialMedia } = footerData;

  return (
    <footer className="snap-align-start">
      <Container className="footer-container" backgroundRGBColor="rgb(214, 0, 28);" width="100%">
        <FooterMainContainer className="footer-mobile-wrapper" flexDirection="column">
          <div className="footer-title-btn-container">
            <div className="footer-title">Come work with us.</div>
            <a href="/careers">
              <div className="footer-icon-main-container">
                <div className="arrow-link">
                  <span className="arrow-wrapper">
                    <span className="small-arrow small-arrow--hover">
                      <img src={rightArrowIcon} alt="rightArrowIcon" />
                    </span>
                    <span className="arrow-link-text">Careers</span>
                  </span>
                  <span className="small-arrow small-arrow--default">
                    <img src={rightArrowIcon} alt="rightArrowIcon" />
                  </span>
                </div>
              </div>
            </a>
          </div>
          {links && (
            <Container flexWrap="wrap">
              {links.map(link => {
                if (link?.links == null) return null;
                return (
                  <Container
                    className="footer-mobile-li-wrapper"
                    margin="70px 11% 156px 0"
                    tabletWidth="50%"
                    tabletMargin="24px 0"
                    flexDirection="column"
                    key={link._key}
                  >
                    <FooterHeadingText>{link.navTitle}</FooterHeadingText>

                    {link.links.map(link => {
                      return <StyledLink {...link} design="textWhite" key={link?._key} />;
                    })}
                  </Container>
                );
              })}
            </Container>
          )}
          {showSocialMedia && socialMediaData?.nav && (
            <GridContainer
              repeat={socialMediaData?.nav.length}
              tabletRepeat={socialMediaData?.nav.length}
              mobileRepeat={socialMediaData?.nav.length}
              columnGap={18}
              width="max-content"
              margin="0 0 24px 0"
            >
              {socialMediaData?.nav.map(item => {
                if (item == null) return null;
                return <SocialIcon data={item} darkBackground key={item._key} />;
              })}
            </GridContainer>
          )}
          {bottomItems && (
            <LinksContainer>
              {bottomItems.map(item => {
                if (item == null) return null;
                if (item?.externalLink == null && item?.internalLink == null) {
                  return <BottomText key={item._key}>{item?.linkText}</BottomText>;
                }
                return <StyledLink {...item} key={item._key} />;
              })}
              <div className="footer-image-container">
                <a href="https://cert.privo.com/#/companies/zuruinc">
                  <img
                    className="footer-first-image"
                    src="https://privohub.privo.com/files/images/PRIVO_Cert/GDPR.png"
                    alt="GDPRkids™ Privacy Assured Program by PRIVO"
                  />
                </a>
                <a href="https://cert.privo.com/#/companies/zuruinc">
                  <img
                    src="https://privohub.privo.com/files/images/PRIVO_Cert/COPPA.png"
                    alt="COPPA Safe Harbor Certification by PRIVO"
                  />
                </a>
              </div>
            </LinksContainer>
          )}
        </FooterMainContainer>
      </Container>
    </footer>
  );
};

export default Footer;
